import React, { useState } from 'react';
import { Box, Grid, Button, MenuItem, FormControl, InputLabel, Select, FormHelperText, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import './OtherDetails.css'

const OtherDetails = ({
  formVals,
  status,
  edit,
  empList,
  customerType,
  formChangeHandler,
  onContinue,
  handleSave,
  getFieldLabel,
  isFieldRequired,
  isFieldVisible,
  customerContacts,
  privatePayerSupportSetting,
  user,
  errors
}) => {
  const primaryBusinessType = user?.comp_settings?.find(s => s.feature_value === 'primaryBusinessType')?.feature_id || null;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  let pManager = !empList?[]:empList.map((emp,indx) => 
      {
          return ({value : emp.id || emp.emp_id, title: (!!emp.employee_name) ? emp.employee_name : emp.emp_first_name+" "+emp.emp_last_name})
      }
      )

  let cusContactList = !customerContacts? [] : customerContacts.map((contact, index) => {
    return ({ value : contact.con_id, title: contact.con_first_name + ' ' + contact. con_last_name})
  })
 
  const customerTypeList = customerType.map((elem, index)=>{
      return ({value : elem, title: elem})
  })
  
  const handleFieldChange = (field) => (e) => {
    const value = e.target?.value;
    formChangeHandler({ target: { id: field, value } });
  };

  const handleClose = () => {
    formChangeHandler({ target: { id: 'status', value: 'Active' } });
    setOpen(false);
  };
  const handleDialogSubmit = () => {
    formChangeHandler({ target: { id: 'status', value: 'Dormant' } });
    setOpen(false);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {isFieldVisible('acquired_date') && (
        <Grid size={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={getFieldLabel('acquired_date','Acquired Date')}
              value={formVals.acquired_date ? dayjs(formVals.acquired_date.replace(/T.*$/, '')) : null}
              onChange={d => {
                if (d && d.isValid()) {
                  // Format as date only, no time or timezone
                  formChangeHandler({
                    target: {
                      id: 'acquired_date',
                      value: d.format('YYYY-MM-DD')
                    }
                  });
                }
              }}
              slotProps={{ textField: { id: 'acquired_date', fullWidth: true, required: isFieldRequired('acquired_date'), error: !!errors.acquired_date } }}
            />
          </LocalizationProvider>
        </Grid>
        )}
        <Grid size={6}>
          <TextField
            id="medicaid_id"
            label="Medicaid ID"
            required={false}
            fullWidth
            value={formVals.medicaid_id || ''}
            onChange={(e,newVal) => formChangeHandler({ target: { id: 'medicaid_id', value: e.target.value || '' } })}
            error={!!errors.status}
          />
        </Grid>
        {isFieldVisible('status') && (
        <Grid size={6}>
          <Autocomplete
            options={status}
            value={formVals.status || null}
            onChange={(e,newVal) => {
              const val = newVal || '';
              if (val === 'Dormant' && !formVals.TransferOutReason) {
                setOpen(true);
              }
              formChangeHandler({ target: { id: 'status', value: val } });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={'Status'}
                required={true}
                disableClearable
                fullWidth
                error={!!errors.status}
              />
            )}
          />
        </Grid>
        )}
        {isFieldVisible('customer_type') && (
        <Grid size={6}>
          <Autocomplete
            options={customerType}
            value={formVals.customer_type || null}
            onChange={(e,newVal) => formChangeHandler({ target: { id: 'customer_type', value: newVal || '' } })}
            renderInput={(params) => (
              <TextField
                {...params}
                label={getFieldLabel('customer_type','Customer Type')}
                required={isFieldRequired('customer_type')}
                fullWidth
                disableClearable
                error={!!errors.customer_type}
              />
            )}
          />
        </Grid>
        )}
        {isFieldVisible('pManager') && (
        <Grid size={6}>
          <Autocomplete
            options={pManager}
            getOptionLabel={(option) => option.title}
            value={pManager.find(pm => pm.value === formVals.pManager) || null}
            onChange={(e,newVal) => formChangeHandler({ target: { id: 'pManager', value: newVal ? newVal.value : '' } })}
            renderInput={(params) => (
              <TextField
                {...params}
                label={getFieldLabel('pManager','Project Manager')}
                required={isFieldRequired('pManager')}
                fullWidth
                error={!!errors.pManager}
              />
            )}
          />
        </Grid>
        )}
        {isFieldVisible('private_payer_responsible') && privatePayerSupportSetting && (
          <Grid size={6}>
            <Autocomplete
              options={[{ value: 'Self', title: 'Self' }, ...cusContactList]}
              getOptionLabel={(option) => option.title}
              value={
                formVals.private_payer_responsible 
                  ? cusContactList.find(p => p.value === formVals.private_payer_responsible) || { value: 'Self', title: 'Self' }
                  : { value: 'Self', title: 'Self' }
              }
              onChange={(e,newVal) => formChangeHandler({ target: { id: 'private_payer_responsible', value: newVal ? newVal.value : 'Self' } })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={getFieldLabel('private_payer_responsible','Private Payer Responsible')}
                  required={isFieldRequired('private_payer_responsible')}
                  fullWidth
                  error={!!errors.private_payer_responsible}
                />
              )}
            />
          </Grid>
        )}
        {['sign_page_date','last_plan_approval_date','last_assess_date','renewal_date','MedicaidEndDate','AgencyAssignDate'].map(field => (
          isFieldVisible(field) && (
          <Grid size={6} key={field}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={getFieldLabel(field, field)}
                value={formVals[field] ? dayjs(formVals[field].replace(/T.*$/, '')) : null}
                onChange={d => {
                  if (d && d.isValid()) {
                    // Format as date only, no time or timezone
                    formChangeHandler({
                      target: {
                        id: field,
                        value: d.format('YYYY-MM-DD')
                      }
                    });
                  }
                }}
                slotProps={{
                  textField: {
                    id: field,
                    required: isFieldRequired(field),
                    fullWidth: true,
                    error: !!errors[field]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          )
        ))}
        {['DDD_tier','diagnose_code','waiver_program','County','Race','Ethnicity','TransferOutReason'].map(field => (
          isFieldVisible(field) && (
          <Grid size={6} key={field}>
            <TextField
              label={getFieldLabel(field, field)}
              id={field}
              value={formVals[field] || ''}
              onChange={handleFieldChange(field)}
              required={isFieldRequired(field)}
              fullWidth
              error={!!errors[field]}
            />
          </Grid>
          )
        ))}
        {isFieldVisible('func_criteria_met') && (
        <Grid size={6}>
          <Autocomplete
            options={[ 'Yes', 'No' ]}
            value={formVals.func_criteria_met === 1 ? 'Yes' : formVals.func_criteria_met === 0 ? 'No' : null}
            onChange={(e,v) => formChangeHandler({ target: { id: 'func_criteria_met', value: v === 'Yes' ? 1 : v === 'No' ? 0 : '' } })}
            renderInput={(params) => (
              <TextField
                {...params}
                label={getFieldLabel('func_criteria_met','Functional Criteria Met')}
                required={isFieldRequired('func_criteria_met')}
                fullWidth
                error={!!errors.func_criteria_met}
              />
            )}
          />
        </Grid>
        )}
        {['NJCATSelfcare','NJCATBehavioral','NJCATMedical','NJCATFCA'].map(field => (
          isFieldVisible(field) && (
          <Grid size={{ xs: 6, md: 6 }} key={field}>
            <TextField
              label={getFieldLabel(field, field)}
              id={field}
              type="number"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              value={formVals[field] || ''}
              onChange={handleFieldChange(field)}
              required={isFieldRequired(field)}
              fullWidth
              error={!!errors[field]}
            />
          </Grid>
          )
        ))}
        {['place01char','place02char','place03char','place04char','place05char','place06char','place07char','place08char'].map(field => (
          isFieldVisible(field) && (
            <Tooltip title={getFieldLabel(field, field)} placement="top">
            <Grid size={{ xs: 6, md: 6 }} key={field}>
              <TextField
                label={getFieldLabel(field, field)}
                id={field}
                type="text"
                inputProps={{ maxLength: 255 }}
                value={formVals[field] || ''}
                onChange={handleFieldChange(field)}
                required={isFieldRequired(field)}
                fullWidth
                error={!!errors[field]}
              />
            </Grid>
            </Tooltip>
          )
        ))}
        {['place09date','place10date','place11date','place12date'].map(field => (
          isFieldVisible(field) && (
            <Tooltip title={getFieldLabel(field, field)} placement="top">
            <Grid size={{ xs: 6, md: 6 }} key={field}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={getFieldLabel(field, field)}
                  value={formVals[field] ? dayjs(typeof(formVals[field]) == "string" ? formVals[field].replace(/[TZ].*$/, '') : formVals[field]) : null}
                  onChange={d => {
                    if (d && d.isValid()) {
                      // Format as date only, no time or timezone
                      formChangeHandler({
                        target: {
                          id: field,
                          value: d.format('YYYY-MM-DD')
                        }
                      });
                    }
                  }}
                  slotProps={{
                    textField: {
                      id: field,
                      required: isFieldRequired(field),
                      fullWidth: true,
                      error: !!errors[field]
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            </Tooltip>
          )
        ))}
        {['place13number','place14number','place15number','place16number'].map(field => (
            isFieldVisible(field) && (
              <Tooltip title={getFieldLabel(field, field)} placement="top">
              <Grid size={{ xs: 6, md: 6 }} key={field}>
                <TextField
                  label={getFieldLabel(field, field)}
                  id={field}
                  type="number"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  value={formVals[field] || ''}
                  onChange={handleFieldChange(field)}
                  required={isFieldRequired(field)}
                  fullWidth
                  error={!!errors[field]}
                />
              </Grid>
              </Tooltip>
            )
          ))}
        {primaryBusinessType === 103 && (
          <>
            <Grid size={6}>
              <Autocomplete
                options={['Yes', 'No']}
                value={formVals.HasPet === 1 ? 'Yes' : formVals.HasPet === 0 ? 'No' : null}
                onChange={(e, v) => formChangeHandler({ target: { id: 'HasPet', value: v === 'Yes' ? 1 : v === 'No' ? 0 : '' } })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Has Pet"
                    fullWidth
                    error={!!errors.HasPet}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <Autocomplete
                options={['Male', 'Female', 'No Preference']}
                value={formVals.ProviderGenderPreference || null}
                onChange={(e, newVal) => formChangeHandler({ target: { id: 'ProviderGenderPreference', value: newVal || '' } })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Provider Gender Preference"
                    fullWidth
                    error={!!errors.ProviderGenderPreference}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label="Language Preference"
                id="LanguagePreference"
                value={formVals.LanguagePreference || ''}
                onChange={handleFieldChange('LanguagePreference')}
                fullWidth
                inputProps={{ maxLength: 50 }}
                error={!!errors.LanguagePreference}
              />
            </Grid>
            <Grid size={6}>
              <Autocomplete
                options={['Yes', 'No']}
                value={formVals.PreferRepeatProviders === 1 ? 'Yes' : formVals.PreferRepeatProviders === 0 ? 'No' : null}
                onChange={(e, v) => formChangeHandler({ target: { id: 'PreferRepeatProviders', value: v === 'Yes' ? 1 : v === 'No' ? 0 : '' } })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Prefer Repeat Providers"
                    fullWidth
                    error={!!errors.PreferRepeatProviders}
                  />
                )}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label="Driver License Number"
                id="DriverLicenseNumber"
                value={formVals.DriverLicenseNumber || ''}
                onChange={handleFieldChange('DriverLicenseNumber')}
                fullWidth
                inputProps={{ maxLength: 50 }}
                error={!!errors.DriverLicenseNumber}
              />
            </Grid>
            <Grid size={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="DL Issue Date"
                  value={formVals.DLIssueDate ? dayjs(formVals.DLIssueDate.replace(/T.*$/, '')) : null}
                  onChange={d => {
                    if (d && d.isValid()) {
                      formChangeHandler({
                        target: {
                          id: 'DLIssueDate',
                          value: d.format('YYYY-MM-DD')
                        }
                      });
                    }
                  }}
                  slotProps={{
                    textField: {
                      id: 'DLIssueDate',
                      fullWidth: true,
                      error: !!errors.DLIssueDate
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="DL Expiry Date"
                  value={formVals.DLExpiryDate ? dayjs(formVals.DLExpiryDate.replace(/T.*$/, '')) : null}
                  onChange={d => {
                    if (d && d.isValid()) {
                      formChangeHandler({
                        target: {
                          id: 'DLExpiryDate',
                          value: d.format('YYYY-MM-DD')
                        }
                      });
                    }
                  }}
                  slotProps={{
                    textField: {
                      id: 'DLExpiryDate',
                      fullWidth: true,
                      error: !!errors.DLExpiryDate
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={6}>
              <TextField
                label="DL Issue State"
                id="DLIssueState"
                value={formVals.DLIssueState || ''}
                onChange={handleFieldChange('DLIssueState')}
                fullWidth
                inputProps={{ maxLength: 2 }}
                placeholder="e.g., NJ"
                error={!!errors.DLIssueState}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Please enter a reason & date below</DialogTitle>
        <DialogContent>
          <Autocomplete
            id="TransferOutReason"
            options={[
              'Individual Passed Away',
              'Switched Providers',
              'Ended Service',
              'Returned to DDD Case Support',
              'Moved to MLTSS',
              'Moved to another state',
              'Disenrolled from DDD',
              'Disapproved'
            ]}
            fullWidth
            value={reason}
            onChange={(e, newVal) => {
              setReason(newVal);
              formChangeHandler({ target: { id: 'TransferOutReason', value: newVal } });
            }}
            renderInput={params => (
              <TextField {...params} label="Reason *" variant="standard" fullWidth />
            )}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date *"
              value={formVals.TransferOutDate ? dayjs(formVals.TransferOutDate.replace(/T.*$/, '')) : null}
              onChange={d => {
                if (d && d.isValid()) {
                  // Format as date only, no time or timezone
                  formChangeHandler({ target: { id: 'TransferOutDate', value: d.format('YYYY-MM-DD') } });
                }
              }}
              slotProps={{ textField: { id: 'TransferOutDate', fullWidth: true } }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleDialogSubmit} disabled={!reason || !formVals.TransferOutDate}>Submit</Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ mt: 2 }}>
        {!edit ? (
          <Button variant="contained" fullWidth onClick={handleSave}>
            Create Customer
          </Button>
        ) : (
          <Button variant="contained" fullWidth onClick={() => onContinue(null, 'customer_tasks')}>
            Continue
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default OtherDetails;
